import type { ResponseItem as ResponseItemType } from '@app/src/types/resourceExplorer'
import { validURL } from '@app/src/utils/helpers'

export type ResponseValueTypes = {
  displayAsFile: boolean
  displayAsOptions: boolean
  displayAsCoordinates: boolean
  selectedOptions: string[]
  hasValue: boolean
}

export const shouldDisplayAsFile = (
  questionType: string,
  item: ResponseItemType,
): ResponseValueTypes['displayAsFile'] => {
  return Boolean(questionType.toLowerCase() === 'file' && item?.answer)
}

export const shouldDisplayAsOptions = (
  questionType: string,
  item: ResponseItemType,
): ResponseValueTypes['displayAsOptions'] => {
  return questionType.toLowerCase() === 'options' && item?.selectedOptions instanceof Array
}

export const getSelectedOptions = (item: ResponseItemType): ResponseValueTypes['selectedOptions'] => {
  return item?.selectedOptions || []
}

export const shouldDisplayAsCoordinates = (
  questionType: string,
  item: ResponseItemType,
): ResponseValueTypes['displayAsCoordinates'] => {
  return questionType.toLowerCase() === 'coordinates' && Boolean(item?.coordinates?.length)
}

export const hasValue = (questionType: string, item: ResponseItemType): boolean => {
  if (shouldDisplayAsCoordinates(questionType, item)) return true
  const displayAsOptions = shouldDisplayAsOptions(questionType, item)
  const selectedOptions = getSelectedOptions(item)
  return displayAsOptions ? Boolean(selectedOptions?.length) : Boolean(item?.answer)
}

export const getResponseValueTypes = (questionType: string, item: ResponseItemType): ResponseValueTypes => {
  return {
    displayAsFile: shouldDisplayAsFile(questionType, item),
    displayAsOptions: shouldDisplayAsOptions(questionType, item),
    displayAsCoordinates: shouldDisplayAsCoordinates(questionType, item),
    selectedOptions: getSelectedOptions(item),
    hasValue: hasValue(questionType, item),
  }
}

const getStableIdFromString = (value: string): string => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

const invalidFileNamePattern = /[<>:"/\\|?*]/

const getFileExtension = (path: string): string => {
  const lastDotIndex = path.lastIndexOf('.')
  if (lastDotIndex === -1) return ''
  return path.slice(lastDotIndex)
}

const getSafeFileNameFromUrl = (url: string): string => {
  try {
    const uri = new URL(url)
    const path = decodeURIComponent(uri.pathname)
    const filename = path.split('/').pop() ?? ''

    if (!filename || invalidFileNamePattern.test(filename)) {
      const ext = getFileExtension(path)
      return `file_${getStableIdFromString(url)}${ext}`
    }

    return filename
  } catch {
    const sanitizedUrl = url.split('?')[0]
    const ext = getFileExtension(sanitizedUrl)
    return `file_${getStableIdFromString(url)}${ext}`
  }
}

const escapeMarkdownText = (value: string): string => value.replace(/([\\[\]()])/g, '\\$1')

const getFileAnswerLinkData = (item: ResponseItemType): { url: string; label: string } | null => {
  if (item?.uploadReferences?.[0]) return null

  const answer = item?.answer?.toString().trim()
  if (!answer || !validURL(answer)) return null

  return {
    url: answer,
    label: getSafeFileNameFromUrl(answer),
  }
}

export const getFileAnswerMarkdown = (item: ResponseItemType): string | null => {
  const linkData = getFileAnswerLinkData(item)
  if (!linkData) return null
  return `[${escapeMarkdownText(linkData.label)}](${linkData.url})`
}
